import axios from 'axios'
import React, { Fragment, useEffect, useState } from 'react'
import jwtDecode from 'jwt-decode'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import DeviceCard from './DeviceCard'
import RemoveDeviceModal from './RemoveDeviceModal'

dayjs.extend(relativeTime)

function DevicesSettings({ activeTab, isDarkMode, compact }) {
	const [loading, setLoading] = useState(false)
	const [currentDevice, setCurrentDevice] = useState(null)
	const [websiteDevices, setWebsiteDevices] = useState([])
	const [mobileDevices, setMobileDevices] = useState([])
	const [extensionDevices, setExtensionDevices] = useState([])
	const [removingDevices, setRemovingDevices] = useState({})
	const [pendingDevice, setPendingDevice] = useState(null)
	const [removeError, setRemoveError] = useState('')

	const fetchDevices = async ({ silent } = {}) => {
		try {
			if (!silent) setLoading(true)
			if (activeTab !== 'devices') return

			const token = localStorage.getItem('jwtToken')
			const user = jwtDecode(token)

			// Fetch website, mobile and extension devices separately
			const [websiteResponse, mobileResponse, extensionResponse] =
				await Promise.all([
					axios.get('/api/user/devices'),
					axios.get('/api/user/devices/mobile').catch(() => ({ data: [] })),
					axios.get('/api/user/devices/extension').catch(() => ({ data: [] })),
				])

			const websiteData = websiteResponse.data || []
			const mobileData = mobileResponse.data || []
			const extensionData = extensionResponse.data || []

			// Find current device from all types
			const allDevices = [...websiteData, ...mobileData, ...extensionData]
			const current = allDevices.find(
				device => device?.deviceId === user?.deviceId
			)

			// Separate other devices by type (excluding current)
			const otherWebsiteDevices = websiteData
				.filter(device => device?.deviceId !== user?.deviceId)
				.sort((a, b) => new Date(b.lastCheckIn) - new Date(a.lastCheckIn))

			const otherMobileDevices = mobileData
				.filter(device => device?.deviceId !== user?.deviceId)
				.sort((a, b) => new Date(b.lastCheckIn) - new Date(a.lastCheckIn))

			const otherExtensionDevices = extensionData
				.filter(device => device?.deviceId !== user?.deviceId)
				.sort((a, b) => new Date(b.lastCheckIn) - new Date(a.lastCheckIn))

			setCurrentDevice(current)
			setWebsiteDevices(otherWebsiteDevices)
			setMobileDevices(otherMobileDevices)
			setExtensionDevices(otherExtensionDevices)
		} catch (error) {
			console.error('Error fetching devices:', error)
		} finally {
			if (!silent) setLoading(false)
		}
	}

	useEffect(() => {
		fetchDevices()
	}, [activeTab])

	const handleRemoveDevice = async deviceId => {
		try {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: true }))
			await axios.post('/api/user/terminate-session', {
				deviceId: deviceId,
			})
			await fetchDevices({ silent: true })
			return true
		} catch (error) {
			console.error('Error removing device:', error)
			return false
		} finally {
			setRemovingDevices(prev => ({ ...prev, [deviceId]: false }))
		}
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center py-32">
				<div className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
					isDarkMode ? 'border-gray-400' : 'border-gray-500'
				}`} />
			</div>
		)
	}

	const allGroups = [
		{ label: 'Website Sessions', type: 'website', devices: websiteDevices },
		{ label: 'Mobile Sessions', type: 'mobile', devices: mobileDevices },
		{ label: 'Extension Sessions', type: 'extension', devices: extensionDevices },
	]

	// On a phone an empty group is three wasted lines. The desktop layout is
	// being reworked separately, so there it still lists every group.
	const deviceGroups = compact
		? allGroups.filter(group => group.devices.length > 0)
		: allGroups

	return (
		<div className={`space-y-3 max-w-4xl mx-auto ${compact ? '' : 'p-6'}`}>
			{currentDevice && (
				<div className="space-y-3">
					<DeviceCard
						device={currentDevice}
						compact={compact}
						isCurrent={true}
						isDarkMode={isDarkMode}
					/>
				</div>
			)}

			{deviceGroups.map(group => (
				<Fragment key={group.label}>
					<div
						className={`border-t ${
							isDarkMode ? 'border-gray-700' : 'border-gray-200'
						}`}
					/>

					<div className="space-y-2">
						<h3
							className={`text-sm font-medium text-${
								isDarkMode ? 'white' : 'gray-900'
							}`}
						>
							{group.label} ({group.devices.length})
						</h3>
						<div className="space-y-2">
							{group.devices.map(device => (
								<DeviceCard
									key={device._id}
									device={device}
									deviceType={group.type}
									compact={compact}
									isCurrent={false}
									isDarkMode={isDarkMode}
									onRemove={() => {
										if (!compact) return handleRemoveDevice(device._id)
										setRemoveError('')
										return setPendingDevice(device)
									}}
									isRemoving={removingDevices[device._id]}
								/>
							))}
						</div>
					</div>
				</Fragment>
			))}

			{pendingDevice && (
				<RemoveDeviceModal
					isDarkMode={isDarkMode}
					isRemoving={Boolean(removingDevices[pendingDevice._id])}
					error={removeError}
					onConfirm={async () => {
						setRemoveError('')
						const removed = await handleRemoveDevice(pendingDevice._id)
						if (removed) setPendingDevice(null)
						else setRemoveError('That did not work. Check your connection and try again.')
					}}
					onClose={() => setPendingDevice(null)}
				/>
			)}
		</div>
	)
}

export default DevicesSettings
